import React from 'react';
import type { FlagKey } from '../registry';
import type { BucketBy } from './FeatureFlagContext';
import { useFeatureVariant } from './useFeatureVariant';
export interface FeatureVariantProps {
  /** The variant flag key to evaluate */
  variantKey: FlagKey;
  /** Bucketing mode: 'user' or 'device' */
  bucketBy: BucketBy;
  /** Map of variant key → content to render */
  variants: Record<string, React.ReactNode>;
  /** Optional fallback when no variant matches */
  fallback?: React.ReactNode;
  /** Optional fallback to render while the provider is initializing (isReady === false).
   *  When omitted, falls back to SSR seed value or empty string (existing behavior). */
  loadingFallback?: React.ReactNode;
  /** Optional attributes for targeting rules (e.g., { email: 'user@zep.us' }) */
  context?: Record<string, string>;
}

/**
 * Declarative component for conditionally rendering content
 * based on a variant feature flag.
 *
 * @example
 * ```tsx
 * <FeatureVariant
 *   variantKey="test-ui-2"
 *   variants={{
 *     '1': <NewDesign />,
 *     '2': <ClassicDesign />,
 *   }}
 *   fallback={<DefaultDesign />}
 * />
 * ```
 */
export function FeatureVariant({
  variantKey,
  bucketBy,
  variants,
  fallback = null,
  loadingFallback,
  context
}: FeatureVariantProps) {
  const {
    variant,
    isReady
  } = useFeatureVariant(variantKey, {
    bucketBy,
    context
  });
  if (!isReady && loadingFallback !== undefined) {
    return <>{loadingFallback}</>;
  }
  if (!variant) return <>{fallback}</>;
  return <>{variants[variant] ?? fallback}</>;
}